import * as React from "react";

import { AutocompleteRenderGroupParams } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { misc } from "app/themes/colors";
import {
  ProductCategoryGroupHeading,
  categoryGroupHeading
} from "components/product-category-filter";

// One Autocomplete group per clinic and category pair. MUI groups only one
// level deep, so the key carries both, and the clinic band (SPA-6232) shows
// only on the first group of each clinic. The synthetic "Check-in new ..." row
// has the empty key and renders no header.
type GroupKey = { clinic: string | null; category: string | null };

export const inventoryGroupKey = (key: GroupKey) => JSON.stringify(key);

const parseGroupKey = (group: string): GroupKey | null =>
  group ? JSON.parse(group) : null;

// The groups whose clinic differs from the group before them, in list order.
export function firstGroupsOfEachClinic(groups: string[]) {
  const firsts = new Set<string>();
  let previousClinic: string | null | undefined;
  groups.forEach((group) => {
    const clinic = parseGroupKey(group)?.clinic ?? null;
    if (clinic !== previousClinic) firsts.add(group);
    previousClinic = clinic;
  });
  return firsts;
}

export default function InventoryProductGroup({
  currentClinicName,
  clinicBandGroups,
  group,
  children
}: AutocompleteRenderGroupParams & {
  currentClinicName?: string;
  clinicBandGroups: Set<string>;
}) {
  const key = parseGroupKey(group);
  const clinic = key?.clinic;
  const showClinicBand = Boolean(clinic) && clinicBandGroups.has(group);
  const categoryHeading = key?.category
    ? categoryGroupHeading(key.category)
    : undefined;
  return (
    <Stack key={group}>
      {showClinicBand && (
        <ListItemText
          data-testid="clinic-group-header"
          sx={{
            p: 1,
            m: 0,
            textAlign: "center",
            fontWeight: 800,
            background: misc.mutedPurple
          }}
        >
          {clinic}
          {clinic === currentClinicName && (
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.secondary", ml: 0.5 }}
            >
              · this clinic
            </Typography>
          )}
        </ListItemText>
      )}
      {categoryHeading && (
        <ProductCategoryGroupHeading>
          {categoryHeading}
        </ProductCategoryGroupHeading>
      )}
      <Box>{children}</Box>
    </Stack>
  );
}
